import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { LoadedWidgetsBySection, SmartCartLayoutSection, SmartCartLayoutSectionId } from '../../smart-cart-form.model';
import {
  API_TYPE_TO_BLOCK_TYPE,
  BLOCK_TYPE_TO_API_TYPE,
  buildWidgetUsageToken,
  HIDDEN_FOR_V1,
  SmartCartBlock,
  SmartCartBlockOption,
  SmartCartBlockType,
  SMART_CART_BLOCK_DEFINITIONS,
} from '../../smart-cart-block.model';
import { createTopBarFormGroup } from '../../widget-editors/top-bar/top-bar.model';
import { createAnnouncementBarFormGroup } from '../../widget-editors/announcement-bar/announcement-bar.model';
import { createGoalsProgressBarFormGroup } from '../../widget-editors/goals-progress-bar/goals-progress-bar.model';
import { createAutoAppliedOrderDiscountWithDisplayConditionsValidator } from '../../widget-editors/goals-progress-bar/goals-progress-bar-auto-apply-validation';
import { createCartSubtotalFormGroup } from '../../widget-editors/cart-subtotal/cart-subtotal.model';
import { createCheckoutAreaFormGroup } from '../../widget-editors/checkout-area/checkout-area.model';
import { createDiscountCodesFormGroup } from '../../widget-editors/discount-codes/discount-codes.model';
import { createCartItemsFormGroup } from '../../widget-editors/cart-items/cart-items.model';
import { createDigitalUpsellFormGroup } from '../../widget-editors/digital-upsell/digital-upsell.model';
import { createUpsellItemsFormGroup } from '../../widget-editors/upsell-items/upsell-items.model';
import { createOrderProtectionFormGroup } from '../../widget-editors/order-protection/order-protection.model';
import { GlobalSettingsFormShape } from '../smart-cart-form-global-settings/global-settings.model';
import { SmartCartWidgetPayload } from '../../../smart-cart.service';
import { PreviewSectionId, PreviewWidget } from '../../preview/widget-previews/widget-preview.types';
import { serializeBlockState } from '../../smart-cart-block-state';
import {
  attachDisplayConditions,
  SUPPORTS_DISPLAY_CONDITIONS,
} from '../smart-cart-form-display-conditions/display-conditions.model';

export interface SmartCartBlockEditEvent {
  block: SmartCartBlock;
  formGroup: FormGroup;
}

@Component({
  selector: 'smart-cart-form-order-management',
  templateUrl: './smart-cart-form-order-management.component.html',
  styleUrls: ['./smart-cart-form-order-management.component.scss'],
  standalone: false,
})
export class SmartCartFormOrderManagementComponent implements OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() loadedWidgets: LoadedWidgetsBySection | null = null;
  @Input() globalSettingsFormGroup?: FormGroup<GlobalSettingsFormShape>;
  @Output() editBlock = new EventEmitter<SmartCartBlockEditEvent>();

  readonly sections: SmartCartLayoutSection[] = [
    { id: 'header', label: 'HEADER' },
    { id: 'body', label: 'BODY' },
    { id: 'footer', label: 'FOOTER' },
  ];
  readonly blockDefinitions = SMART_CART_BLOCK_DEFINITIONS;

  blocksBySection: Record<SmartCartLayoutSectionId, SmartCartBlock[]> = {
    header: [],
    body: [],
    footer: [],
  };
  availableBySection: Record<SmartCartLayoutSectionId, SmartCartBlockOption[]> = {
    header: [],
    body: [],
    footer: [],
  };
  settingsByBlockId: Record<string, FormGroup> = {};
  /** Blocks whose settings/enabled state differ from what was loaded (or from their defaults when added). */
  changedByBlockId: Record<string, boolean> = {};

  private baselineByBlockId: Record<string, string> = {};
  private blockCounter = 0;
  private seeded = false;
  private readonly rulesDynamicFormService = inject(RulesDynamicFormService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loadedWidgets'] && this.loadedWidgets && !this.seeded) {
      this.seedBlocks(this.loadedWidgets);
      this.recomputeAvailableBlocks();
      this.seeded = true;
    }
  }

  onEditBlock(block: SmartCartBlock): void {
    const formGroup = this.settingsByBlockId[block.id];
    if (!formGroup) return;
    this.editBlock.emit({ block, formGroup });
  }

  onToggleEnabled(block: SmartCartBlock, enabled: boolean): void {
    block.enabled = enabled;
    this.settingsByBlockId[block.id]?.updateValueAndValidity();
    this.refreshChangedState(block.id);
  }

  /** Re-compares a block against its baseline; the parent calls this when the widget editor closes. */
  refreshChangedState(blockId: string): void {
    const block = this.findBlock(blockId);
    const formGroup = this.settingsByBlockId[blockId];
    if (!block || !formGroup) return;
    this.changedByBlockId[blockId] = serializeBlockState(formGroup, block.enabled) !== this.baselineByBlockId[blockId];
  }

  getWidgetsBySection(): Record<SmartCartLayoutSectionId, SmartCartWidgetPayload[]> {
    const result: Record<SmartCartLayoutSectionId, SmartCartWidgetPayload[]> = {
      header: [],
      body: [],
      footer: [],
    };

    for (const { id: sectionId } of this.sections) {
      result[sectionId] = this.blocksBySection[sectionId]
        .map((block, index) => this.buildWidgetPayload(block, index))
        .filter((payload): payload is SmartCartWidgetPayload => payload !== null);
    }

    return result;
  }

  // Returns block.type as the enum (not the API string) so the preview dispatcher can @switch
  // on it directly. Called every change-detection cycle — keep cheap.
  getPreviewWidgetsBySection(): Record<PreviewSectionId, PreviewWidget[]> {
    const result: Record<PreviewSectionId, PreviewWidget[]> = { header: [], body: [], footer: [] };

    for (const { id: sectionId } of this.sections) {
      result[sectionId] = this.blocksBySection[sectionId].map((block) => {
        const formGroup = this.settingsByBlockId[block.id];
        const settings = formGroup ? (formGroup.getRawValue() as Record<string, unknown>) : {};
        return { id: block.id, type: block.type, enabled: block.enabled, settings };
      });
    }

    return result;
  }

  private buildWidgetPayload(block: SmartCartBlock, index: number): SmartCartWidgetPayload | null {
    const apiType = BLOCK_TYPE_TO_API_TYPE[block.type];
    if (!apiType) return null;

    const formGroup = this.settingsByBlockId[block.id];
    const settings = formGroup ? (this.stripFileValues(formGroup.getRawValue()) as Record<string, unknown>) : {};

    return {
      ...(block.widgetDbId !== undefined ? { id: block.widgetDbId } : {}),
      type: apiType,
      enabled: block.enabled,
      order: index,
      usageToken: block.usageToken,
      settings,
    };
  }

  private stripFileValues(value: unknown): unknown {
    if (value instanceof File || value instanceof Blob) return '';
    if (Array.isArray(value)) return value.map((item) => this.stripFileValues(item));
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, this.stripFileValues(val)]));
    }
    return value;
  }

  onAddBlock(sectionId: SmartCartLayoutSectionId, type: SmartCartBlockType): void {
    const block = this.createBlock(type);
    this.registerBlockSettings(block, this.createFormGroupForType(type));
    this.blocksBySection[sectionId] = [...this.blocksBySection[sectionId], block];
    this.recomputeAvailableBlocks();
  }

  onRemoveBlock(sectionId: SmartCartLayoutSectionId, block: SmartCartBlock): void {
    this.blocksBySection[sectionId] = this.blocksBySection[sectionId].filter((item) => item.id !== block.id);
    this.unregisterBlockSettings(block.id);
    this.recomputeAvailableBlocks();
  }

  onDrop(sectionId: SmartCartLayoutSectionId, event: CdkDragDrop<SmartCartBlock[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const list = [...this.blocksBySection[sectionId]];
    moveItemInArray(list, event.previousIndex, event.currentIndex);
    this.blocksBySection[sectionId] = list;
  }

  private seedBlocks(loaded: LoadedWidgetsBySection): void {
    const hasDbWidgets = this.sections.some(({ id }) => loaded[id].length > 0);
    if (hasDbWidgets) {
      this.seedFromDb(loaded);
    } else {
      this.seedDefaults();
    }
  }

  private seedFromDb(loaded: LoadedWidgetsBySection): void {
    for (const { id } of this.sections) {
      const widgets = [...loaded[id]].sort((a, b) => a.order - b.order);
      const blocks: SmartCartBlock[] = [];
      for (const widget of widgets) {
        const blockType = API_TYPE_TO_BLOCK_TYPE[widget.type];
        if (!blockType) continue;
        const block = this.createBlock(blockType, widget.enabled);
        block.widgetDbId = widget.id;
        block.usageToken = widget.usageToken || block.usageToken;
        this.registerBlockSettings(block, this.createFormGroupForType(blockType, widget.settings));
        blocks.push(block);
      }
      this.blocksBySection[id] = blocks;
    }
  }

  private seedDefaults(): void {
    Object.values(this.blockDefinitions).forEach((def) => {
      if (!def.autoSection) return;
      const block = this.createBlock(def.type);
      this.registerBlockSettings(block, this.createFormGroupForType(def.type));
      this.blocksBySection[def.autoSection] = [...this.blocksBySection[def.autoSection], block];
    });
  }

  private createBlock(type: SmartCartBlockType, enabled = true): SmartCartBlock {
    return { id: `${type}-${++this.blockCounter}`, type, enabled, usageToken: buildWidgetUsageToken(type) };
  }

  /** Nested group on the parent form so block-level validity propagates to the Save button. */
  private get blockSettingsGroup(): FormGroup {
    let group = this.formGroup.get('blockSettings') as FormGroup | null;
    if (!group) {
      group = new FormGroup({});
      this.formGroup.addControl('blockSettings', group);
    }
    return group;
  }

  private registerBlockSettings(block: SmartCartBlock, group: FormGroup): void {
    if (block.type === SmartCartBlockType.GOALS_PROGRESS_BAR) {
      group.addValidators(createAutoAppliedOrderDiscountWithDisplayConditionsValidator(() => block.enabled));
      group.updateValueAndValidity();
    }
    this.settingsByBlockId[block.id] = group;
    this.blockSettingsGroup.setControl(block.id, group);
    this.baselineByBlockId[block.id] = serializeBlockState(group, block.enabled);
  }

  private unregisterBlockSettings(blockId: string): void {
    delete this.settingsByBlockId[blockId];
    delete this.baselineByBlockId[blockId];
    delete this.changedByBlockId[blockId];
    this.blockSettingsGroup.removeControl(blockId);
  }

  private findBlock(blockId: string): SmartCartBlock | undefined {
    for (const { id } of this.sections) {
      const block = this.blocksBySection[id].find((item) => item.id === blockId);
      if (block) return block;
    }
    return undefined;
  }

  private createFormGroupForType(type: SmartCartBlockType, initialSettings?: Record<string, unknown>): FormGroup {
    const formGroup = this.createSettingsFormGroup(type, initialSettings);
    if (!SUPPORTS_DISPLAY_CONDITIONS.has(type)) return formGroup;
    return attachDisplayConditions(formGroup, initialSettings, this.rulesDynamicFormService);
  }

  private createSettingsFormGroup(type: SmartCartBlockType, initialSettings?: Record<string, unknown>): FormGroup {
    switch (type) {
      case SmartCartBlockType.TOP_BAR:
        return createTopBarFormGroup(initialSettings ?? null);
      case SmartCartBlockType.ANNOUNCEMENT_BAR:
        return createAnnouncementBarFormGroup(initialSettings ?? null);
      case SmartCartBlockType.GOALS_PROGRESS_BAR:
        return createGoalsProgressBarFormGroup(initialSettings ?? null);
      case SmartCartBlockType.CART_SUBTOTAL:
        return createCartSubtotalFormGroup(initialSettings ?? null);
      case SmartCartBlockType.CHECKOUT_AREA:
        return createCheckoutAreaFormGroup(initialSettings ?? null);
      case SmartCartBlockType.DISCOUNT_CODES:
        return createDiscountCodesFormGroup(initialSettings ?? null);
      case SmartCartBlockType.CART_ITEMS:
        return createCartItemsFormGroup(initialSettings ?? null);
      case SmartCartBlockType.DIGITAL_UPSELL:
        return createDigitalUpsellFormGroup(initialSettings ?? null);
      case SmartCartBlockType.UPSELL_ITEMS:
        return createUpsellItemsFormGroup(initialSettings ?? null, this.rulesDynamicFormService);
      case SmartCartBlockType.ORDER_PROTECTION:
        return createOrderProtectionFormGroup(initialSettings ?? null);
      default:
        // Other widget types don't have editors yet — give them an empty form group as a placeholder.
        return new FormGroup({});
    }
  }

  private recomputeAvailableBlocks(): void {
    const usedTypes = new Set<SmartCartBlockType>();
    Object.values(this.blocksBySection).forEach((list) => list.forEach((block) => usedTypes.add(block.type)));

    this.sections.forEach(({ id }) => {
      this.availableBySection[id] = Object.values(this.blockDefinitions)
        .filter((def) => def.allowedSections.includes(id) && !HIDDEN_FOR_V1.has(def.type))
        .map((def) => ({
          type: def.type,
          label: def.label,
          disabled: def.unique && usedTypes.has(def.type),
        }));
    });
  }
}
